import type { HyperguestHotel } from '@travel-planner/shared/schemas';
import { Hotel, MapPin, Sparkles } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import {
  Button,
  EmptyState,
  Pagination,
  PaginationButton,
  Spinner,
  Text,
  Tooltip,
} from '@/shared/ui';
import { scoreToLabel } from '../model/ai-score';
import { hotelRowDetails } from '../model/row-details';
import { formatLocation } from '../model/title';
import css from './hyperguest-mapper.module.scss';
import { detailTooltipBubble, RowDetailTooltip } from './row-detail-tooltip';

const rowStyles = pickCss(css, css.mapperRow);
const badgeStyles = pickCss(css, css.aiBadge);

type Props = {
  rows: HyperguestHotel[];
  totalPages: number;
  currentPage: number;
  selectedExternalId: number | null;
  scores: Map<string, number>;
  loading: boolean;
  onSelect: (externalId: number) => void;
  onPageChange: (page: number) => void;
};

export const HyperguestHotelsTable = ({
  rows,
  totalPages,
  currentPage,
  selectedExternalId,
  scores,
  loading,
  onSelect,
  onPageChange,
}: Props) => (
  <div className={css.tablePane}>
    {loading ? (
      <Spinner />
    ) : rows.length === 0 ? (
      <EmptyState
        title="No HyperGuest hotels"
        description="Run the import to populate the static catalogue."
      />
    ) : (
      <>
        <ul className={css.cardList}>
          {rows.map((row) => {
            const isSelected = row.externalId === selectedExternalId;
            const score = scores.get(row.id);
            const badge = score !== undefined ? scoreToLabel(score) : null;
            return (
              <li className={rowStyles({ isSelected })} key={row.id}>
                <div className={css.rowMain}>
                  <Tooltip
                    position="bottom"
                    bubbleClassName={detailTooltipBubble}
                    content={
                      <RowDetailTooltip details={hotelRowDetails(row)} />
                    }
                  >
                    <div className={css.metaLine}>
                      <Hotel className={css.metaIcon} />
                      <Text weight="semibold" size="sm">
                        {row.title}
                      </Text>
                    </div>
                  </Tooltip>
                  <div className={css.metaLine}>
                    <MapPin className={css.metaIcon} />
                    <Text variant="muted" size="xs">
                      {formatLocation(row.city, row.country)}
                    </Text>
                  </div>
                  {badge && (
                    <span className={badgeStyles({ variant: badge.variant })}>
                      <Sparkles className={css.metaIcon} />
                      {badge.label} ({badge.percent}%)
                    </span>
                  )}
                </div>
                <div className={css.rowActions}>
                  <Button
                    size="sm"
                    variant={isSelected ? 'primary' : 'outline'}
                    onClick={() => onSelect(row.externalId)}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        {totalPages > 1 && (
          <Pagination>
            <PaginationButton
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Prev
            </PaginationButton>
            <Text size="sm" variant="muted">
              Page {currentPage} of {totalPages}
            </Text>
            <PaginationButton
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next
            </PaginationButton>
          </Pagination>
        )}
      </>
    )}
  </div>
);
